import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { coerceCssPixelValue } from '@angular/cdk/coercion';
import { ViewportRuler } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  ViewRef,
  DOCUMENT
} from '@angular/core';
import { PhotoswipeService } from '@df/ui/photoswipe/photoswipe.service';
import { Photoswipe, PhotoswipeOptions } from '@df/ui/photoswipe/photoswipe.types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

let photoswipeGalleryUid = 1;

@Component({
    selector: 'photoswipe-lightbox',
    templateUrl: './photoswipe-lightbox.component.html',
    styleUrls: ['./photoswipe-lightbox.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('itemAnimation', [
            transition('* => *', [
                query(':enter', style({ opacity: 0 }), { optional: true }),
                query(':enter', stagger('150ms', [animate('250ms ease-in', style({ opacity: 1 }))]), { optional: true }),
                query(':leave', style({ opacity: 0 }), { optional: true })
            ])
        ])
    ],
    standalone: true,
    imports: [NgTemplateOutlet],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhotoswipeLightboxComponent implements AfterViewInit, OnInit, OnDestroy, OnChanges {
    private _previousHTMLStyles = { top: '', left: '' };
    private _previousScrollPosition?: {
        top: number;
        left: number;
    };
    private _isEnabled = false;
    private _document: Document;

    @Input()
    photoswipeOptions: Partial<PhotoswipeOptions> = {};

    @Output()
    photoswipeChanged = new EventEmitter<number>();

    @Output()
    photoswipeStateChange = new EventEmitter<boolean>();

    @ViewChild('photoswipeRef')
    photoswipeElementRef!: ElementRef;

    @ContentChild('photoswipeIconCloseTpl', { static: false })
    photoswipeIconCloseTpl!: TemplateRef<any>;

    @ContentChild('photoswipeIconZoomTpl', { static: false })
    photoswipeIconZoomTpl!: TemplateRef<any>;

    @ContentChild('photoswipeIconNextTpl', { static: false })
    photoswipeIconNextTpl!: TemplateRef<any>;

    @ContentChild('photoswipeIconPrevTpl', { static: false })
    photoswipeIconPrevTpl!: TemplateRef<any>;

    @ContentChild('photoswipeThumbnailsTpl', { static: false })
    photoswipeThumbnailsTpl!: TemplateRef<any>;

    @ContentChild('photoswipeThumbnailTpl', { static: false })
    photoswipeThumbnailTpl!: TemplateRef<any>;

    @ContentChild('photoswipeTopbarTpl', { static: false })
    photoswipeTopbarTpl!: TemplateRef<any>;

    protected ngUnsubscribe$ = new Subject<void>();
    protected galleryUID = photoswipeGalleryUid++;
    state = false;

    constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        protected viewportRuler: ViewportRuler,
        protected photoswipeService: PhotoswipeService,
        @Inject(DOCUMENT) docRef: Document
    ) {
        this._document = docRef;
    }

    ngOnInit(): void {
        this.photoswipeService.state$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(value => {
            if (value) {
                this.open();
            } else {
                this.close();
            }

            this.photoswipeStateChange.emit(value);
            this.detectChanges();
        });

        this.photoswipeService.changed$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(index => {
            this.photoswipeChanged.next(index);
            this.detectChanges();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['photoswipeOptions']) {
            const options = {
                galleryUID: this.galleryUID,
                ...changes['photoswipeOptions'].currentValue
            };

            this.photoswipeService.updateOptions(options);
        }
    }

    ngAfterViewInit(): void {
        this.photoswipeService.photoswipeElementRef = this.photoswipeElementRef;
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.disable();
    }

    closeLightbox(): void {
        if (this.photoswipe.options.closeOnHistoryBack) {
            const historyState = window.history.state;

            if (historyState?.photoswipeOpen) {
                history.back();
            } else {
                this.photoswipe.close();
            }
        } else {
            this.photoswipe.close();
        }
    }

    protected open(): void {
        this.state = true;

        if (this.photoswipeOptions?.blockScroll) {
            this.enable();
        }
    }

    protected close(): void {
        this.state = false;

        if (this.photoswipeOptions?.blockScroll) {
            this.disable();
        }
    }

    get photoswipe(): Photoswipe<PhotoswipeOptions> {
        return this.photoswipeService.photoswipe;
    }

    detectChanges(): void {
        if (!(<ViewRef>this.changeDetectorRef).destroyed) {
            this.changeDetectorRef.detectChanges();
        }
    }

    /** Blocks page-level scroll while the attached overlay is open. */
    enable(): void {
        if (this._canBeEnabled()) {
            const root = this._document.documentElement;

            this._previousScrollPosition = this.viewportRuler.getViewportScrollPosition();

            // Cache the previous inline styles in case the user had set them.
            this._previousHTMLStyles.left = root.style.left || '';
            this._previousHTMLStyles.top = root.style.top || '';

            // Note: we're using the `html` node, instead of the `body`, because the `body` may
            // have the user agent margin, whereas the `html` is guaranteed not to have one.
            root.style.left = coerceCssPixelValue(-this._previousScrollPosition.left);
            root.style.top = coerceCssPixelValue(-this._previousScrollPosition.top);
            root.classList.add('cdk-global-scrollblock');
            this._isEnabled = true;
        }
    }

    /** Unblocks page-level scroll while the attached overlay is open. */
    disable(): void {
        if (this._isEnabled) {
            const html = this._document.documentElement;
            const body = this._document.body;
            const htmlStyle = html.style as CSSStyleDeclaration;
            const bodyStyle = body.style as CSSStyleDeclaration;
            const previousHtmlScrollBehavior = htmlStyle.scrollBehavior || '';
            const previousBodyScrollBehavior = bodyStyle.scrollBehavior || '';

            this._isEnabled = false;

            htmlStyle.left = this._previousHTMLStyles.left;
            htmlStyle.top = this._previousHTMLStyles.top;
            html.classList.remove('cdk-global-scrollblock');

            // Disable user-defined smooth scrolling temporarily while we restore the scroll position.
            // See https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-behavior
            htmlStyle.scrollBehavior = bodyStyle.scrollBehavior = 'auto';

            if (this._previousScrollPosition) {
                window.scroll(this._previousScrollPosition.left, this._previousScrollPosition.top);
            }

            htmlStyle.scrollBehavior = previousHtmlScrollBehavior;
            bodyStyle.scrollBehavior = previousBodyScrollBehavior;
        }
    }

    private _canBeEnabled(): boolean {
        // Since the scroll strategies can't be singletons, we have to use a global CSS class
        // (`cdk-global-scrollblock`) to make sure that we don't try to disable global
        // scrolling multiple times.
        const html = this._document.documentElement;

        if (html.classList.contains('cdk-global-scrollblock') || this._isEnabled) {
            return false;
        }

        const body = this._document.body;
        const viewport = this.viewportRuler.getViewportSize();
        return body.scrollHeight > viewport.height || body.scrollWidth > viewport.width;
    }
}
